import { DocumentData, FirestoreDataConverter } from 'firebase/firestore'
import { Material } from './material'
import { Labor } from './labor'
import { Message } from './message'
import { toNumber } from '@/utils'

export const BUDGET_STATUS = {
  DRAFT: 'draft',
  AWAITING: 'awaiting_acceptance',
  ACCEPTED: 'accepted',
  ARCHIVE: 'arhive'
}

export const BUDGET_ITEM_STATUS = {
  ACCEPTED: 'accepted',
  SCHEDULED: 'scheduled',
  IN_PROGRESS: 'in_progress',
  ON_HOLD: 'on_hold',
  COMPLETED: 'completed'
}

export class BudgetItemUpgrade {
  id: string
  name: string
  description: string
  cost: number
}

export class CostItem {
  materials: Material[]
  labor: Labor[]

  constructor () {
    this.materials = []
    this.labor = []
  }
}

export class BudgetItem {
  id: string
  weight: number
  title: string
  scopeOfWork: string
  cost: number
  margin: number
  unitPrice: number
  quantity: number
  totalPrice: number
  locked: boolean
  status: string
  labor: Labor[]
  laborBudget: number
  materials: Material[]
  materialsBudget: number
  isCreditLineItem: boolean

  category: {
    name: string;
    weight: number;
  }

  attachments: string[]

  // proposal attributes
  optional: boolean
  selected: boolean
  messages: Message[]
  // upgrades: BudgetItemUpgrade[]

  // scheduling attributes
  dependsOn: string // Another item ID
  startDate: Date
  endDate: Date
  assignedTo: string

  constructor () {
    this.title = ''
    this.weight = 0
    this.scopeOfWork = ''
    this.cost = 0
    this.margin = -1
    this.unitPrice = 0
    this.quantity = 1
    this.totalPrice = 0
    this.category = {
      name: '',
      weight: 0
    }
    this.weight = 0
    this.materials = []
    this.labor = []
    this.attachments = []
    this.optional = false
    this.selected = true
    this.status = null
    this.isCreditLineItem = false
  }
}

export class Budget {
  id: string
  projectId: string
  status: string
  mode: string
  lineItems: BudgetItem[]
  createdAt: Date
  updatedAt: Date

  constructor () {
    this.id = null
    this.projectId = ''
    this.status = BUDGET_STATUS.DRAFT
    this.mode = 'edit'
    this.lineItems = []
    this.createdAt = new Date()
    this.updatedAt = new Date()
  }

  getBudgetTotalPrice () {
    let total = 0
    this.lineItems.map((item) => {
      const price = typeof item.totalPrice === 'string' ? toNumber(item.totalPrice) : item.totalPrice
      total += price
    })
    return total
  }

  getLaborCost () {
    let laborCost = 0
    this.lineItems.map((item) => {
      if (item.optional && !item.selected) {
        laborCost += 0
      } else {
        const cost = typeof item.laborBudget === 'string' ? toNumber(item.laborBudget) : item.laborBudget
        laborCost += cost
      }
    })
    return laborCost
  }

  getMaterialCost () {
    let materialsCost = 0
    this.lineItems.map((item) => {
      if (item.optional && !item.selected) {
        materialsCost += 0
      } else {
        const cost = typeof item.materialsBudget === 'string' ? toNumber(item.materialsBudget) : item.materialsBudget
        materialsCost += cost
      }
    })
    return materialsCost
  }

  getTotalCost () {
    return this.getLaborCost() + this.getMaterialCost()
  }

  getTotalPrice () {
    let totalPrice = 0
    this.lineItems.map((item) => {
      if (item.optional && !item.selected) return
      const price = typeof item.totalPrice === 'string' ? toNumber(item.totalPrice) : item.totalPrice
      totalPrice += price
    })
    return totalPrice
  }

  getCategoryTotalPrice (categoryName: string) {
    let total = 0
    this.lineItems.map((item) => {
      if (item.category.name === categoryName) {
        if (item.optional && !item.selected) return
        const price = typeof item.totalPrice === 'string' ? toNumber(item.totalPrice) : item.totalPrice
        total += price
      }
    })
    return total
  }
}

export const budgetConverter: FirestoreDataConverter<Budget, DocumentData> = {
  toFirestore: (budget: Budget) => {
    budget.createdAt = budget.createdAt ? new Date(budget.createdAt) : new Date()
    budget.updatedAt = new Date()
    return Object.assign({}, JSON.parse(JSON.stringify(budget)))
  },

  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options)
    data.createdAt = (typeof data.createdAt === 'object') ? data.createdAt.toDate() : data.createdAt
    data.updatedAt = (typeof data.updatedAt === 'object') ? data.updatedAt.toDate() : data.updatedAt
    return Object.assign(new Budget(), JSON.parse(JSON.stringify(data))) as Budget
  }
}

export const budgetItemConverter: FirestoreDataConverter<BudgetItem, DocumentData> = {
  toFirestore: (budgetItem: BudgetItem) => {
    budgetItem.cost = typeof budgetItem.cost === 'string' ? toNumber(budgetItem.cost) : budgetItem.cost
    budgetItem.quantity = typeof budgetItem.quantity === 'string' ? toNumber(budgetItem.quantity) : budgetItem.quantity
    budgetItem.unitPrice = typeof budgetItem.unitPrice === 'string' ? toNumber(budgetItem.unitPrice) : budgetItem.unitPrice
    budgetItem.totalPrice = typeof budgetItem.totalPrice === 'string' ? toNumber(budgetItem.totalPrice) : budgetItem.totalPrice
    budgetItem.materialsBudget = typeof budgetItem.materialsBudget === 'string' ? toNumber(budgetItem.materialsBudget) : budgetItem.materialsBudget
    budgetItem.laborBudget = typeof budgetItem.laborBudget === 'string' ? toNumber(budgetItem.laborBudget) : budgetItem.laborBudget

    if (budgetItem.materials) {
      budgetItem.materials = budgetItem.materials.map((material) => {
        material.cost = typeof material.cost === 'string' ? toNumber(material.cost) : material.cost
        material.quantity = typeof material.quantity === 'string' ? toNumber(material.quantity) : material.quantity
        return material
      })
    }

    if (budgetItem.labor) {
      budgetItem.labor = budgetItem.labor.map((labor) => {
        labor.hourlyRate = typeof labor.hourlyRate === 'string' ? toNumber(labor.hourlyRate) : labor.hourlyRate
        labor.hours = typeof labor.hours === 'string' ? toNumber(labor.hours) : labor.hours
        return labor
      })
    }

    return Object.assign({}, JSON.parse(JSON.stringify(budgetItem)))
  },

  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options)
    data.date = (typeof data.date === 'object') ? data.date.toDate() : data.date

    return Object.assign(new BudgetItem(), JSON.parse(JSON.stringify(data))) as BudgetItem
  }
}
